import { useMemo } from "react";
import { opsAPI } from "./opsApi";
import { toast } from "sonner";
import CountUp from "./CountUp";
import HealthGauge from "./HealthGauge";
import {
  Flame, ArrowUpRight, CheckCircle2, Circle, Bed, Users, Wrench, Package,
  Sparkles, Zap, Clock, ArrowRight,
} from "lucide-react";

/**
 * Today's Focus — the centerpiece of AI Operations Center.
 *
 * Blends three inputs deterministically:
 *   1. Open insights (severity × confidence)
 *   2. Critical live KPIs (dirty rooms, VIP arrivals, pending check-ins, complaints, OOO, engineering tickets)
 *   3. Progress vs. yesterday (todays_revenue, occupancy_pct)
 *
 * Produces up to 5 ranked focus items. If there is truly nothing to attend to,
 * the panel congratulates the operator with an "All clear" mode.
 */

const MODULE_META = {
  fo:  { icon: Users,    color: "#38bdf8", to: "/fo" },
  hk:  { icon: Sparkles, color: "#f59e0b", to: "/housekeeping" },
  eng: { icon: Wrench,   color: "#fb923c", to: "/engineering" },
  engineering: { icon: Wrench, color: "#fb923c", to: "/engineering" },
  inv: { icon: Package,  color: "#e879f9", to: "/inventory" },
  inventory: { icon: Package, color: "#e879f9", to: "/inventory" },
  wf:  { icon: Users,    color: "#10b981", to: "/wf" },
  ops: { icon: Zap,      color: "#a78bfa", to: "/operations" },
  default: { icon: Zap,  color: "#a78bfa", to: "/operations" },
};

const SEV_WEIGHT = { critical: 100, high: 70, medium: 40, low: 15, info: 5 };


function buildFocusItems(insights = [], kpis = {}, health = {}) {
  const items = [];

  // 1. Convert open insights → focus items
  for (const i of insights) {
    const w = (SEV_WEIGHT[i.severity] ?? 5) * (0.5 + 0.5 * (i.confidence ?? 0.8));
    items.push({
      key: `insight:${i.id}`,
      kind: "insight",
      weight: w,
      severity: i.severity,
      module: i.module,
      title: i.title,
      subtitle: i.reasoning,
      action: i.recommendation || "Review inside the module",
      to: (MODULE_META[i.module] || MODULE_META.default).to,
      impact_currency: i.impact_currency,
      confidence: i.confidence,
      insight_id: i.id,
    });
  }

  // 2. Derive from KPIs directly (things that never made it into an insight)
  if ((kpis.pending_checkins || 0) >= 5) {
    items.push({
      key: "kpi:checkins", kind: "kpi",
      weight: 60 + Math.min(30, kpis.pending_checkins * 2),
      severity: "high", module: "fo",
      title: `${kpis.pending_checkins} check-ins waiting`,
      subtitle: "Front desk queue is building. Consider opening a second station.",
      action: "Open Front Desk", to: "/fo",
    });
  }
  if ((kpis.vip_arrivals_today || 0) > 0) {
    items.push({
      key: "kpi:vip", kind: "kpi",
      weight: 55 + kpis.vip_arrivals_today * 5,
      severity: "medium", module: "fo",
      title: `${kpis.vip_arrivals_today} VIP arrival${kpis.vip_arrivals_today === 1 ? "" : "s"} today`,
      subtitle: "Confirm room readiness and welcome amenities.",
      action: "See arrivals", to: "/arrivals",
    });
  }
  if ((kpis.rooms_dirty || 0) >= 8) {
    items.push({
      key: "kpi:hk", kind: "kpi",
      weight: 50 + Math.min(40, kpis.rooms_dirty),
      severity: kpis.rooms_dirty > 20 ? "high" : "medium", module: "hk",
      title: `${kpis.rooms_dirty} dirty rooms in the pipeline`,
      subtitle: "Housekeeping backlog may impact same-day check-ins.",
      action: "Open HK Board", to: "/housekeeping",
    });
  }
  if ((kpis.guest_complaints || 0) > 0) {
    items.push({
      key: "kpi:complaints", kind: "kpi",
      weight: 65 + kpis.guest_complaints * 8,
      severity: "high", module: "fo",
      title: `${kpis.guest_complaints} guest complaint${kpis.guest_complaints === 1 ? "" : "s"} open`,
      subtitle: "Guest satisfaction risk — respond within the hour.",
      action: "Handle complaints", to: "/fo",
    });
  }
  if ((kpis.engineering_tickets || 0) >= 5) {
    items.push({
      key: "kpi:eng", kind: "kpi",
      weight: 45 + kpis.engineering_tickets * 3,
      severity: "medium", module: "engineering",
      title: `${kpis.engineering_tickets} maintenance jobs open`,
      subtitle: "Consider re-balancing tickets across on-shift engineers.",
      action: "Open Engineering", to: "/engineering",
    });
  }
  if ((kpis.rooms_out_of_order || 0) >= 2) {
    items.push({
      key: "kpi:ooo", kind: "kpi",
      weight: 40 + kpis.rooms_out_of_order * 6,
      severity: "medium", module: "engineering",
      title: `${kpis.rooms_out_of_order} rooms out of order`,
      subtitle: "Lost revenue potential — expedite repairs.",
      action: "Open Engineering", to: "/engineering",
    });
  }
  if ((kpis.pending_approvals || 0) > 0) {
    items.push({
      key: "kpi:approvals", kind: "kpi",
      weight: 35 + kpis.pending_approvals * 4,
      severity: "low", module: "ops",
      title: `${kpis.pending_approvals} approval${kpis.pending_approvals === 1 ? "" : "s"} waiting for you`,
      subtitle: "Delegations respected — approve to keep operations moving.",
      action: "Review approvals", to: "/wf/permissions",
    });
  }

  // Dedupe by kind+module+title
  const seen = new Set();
  const unique = items.filter((x) => {
    const k = `${x.kind}:${x.module}:${x.title.toLowerCase()}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });

  unique.sort((a, b) => b.weight - a.weight);
  return unique.slice(0, 5);
}


export default function TodaysFocus({ data, onInsightAcked, onNavigate }) {
  const insights = data?.insights || [];
  const kpis = data?.kpis || {};
  const health = data?.health || {};

  const items = useMemo(
    () => buildFocusItems(insights, kpis, health),
    [insights, kpis, health]
  );

  const ackInsight = async (insightId) => {
    try {
      await opsAPI.insightAction(insightId, "acknowledge");
      toast.success("Handled");
      onInsightAcked?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to acknowledge");
    }
  };

  return (
    <section
      data-testid="todays-focus"
      className="relative rounded-3xl overflow-hidden border border-white/[0.14]"
      style={{
        background: "linear-gradient(135deg, rgba(15,21,48,0.85) 0%, rgba(15,21,48,0.75) 45%, rgba(15,21,48,0.85) 100%)",
        backdropFilter: "blur(20px)",
      }}
    >
      {/* Subtle accent glow */}
      <div className="pointer-events-none absolute inset-0"
        style={{
          background: "radial-gradient(circle at 20% 30%, rgba(139,92,246,0.10), transparent 40%), radial-gradient(circle at 80% 70%, rgba(245,158,11,0.08), transparent 40%)",
        }}
      />
      {/* Grain / noise overlay */}
      <div className="pointer-events-none absolute inset-0 opacity-[0.04]"
        style={{ backgroundImage: "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.6'/%3E%3C/svg%3E\")" }}
      />

      <div className="relative grid grid-cols-1 lg:grid-cols-[260px_1fr_240px]">
        {/* ─── LEFT: Health at a glance ─── */}
        <div className="p-6 lg:p-7 border-b lg:border-b-0 lg:border-r border-white/[0.10] flex flex-col items-center justify-center gap-4">
          <div className="text-[10.5px] uppercase tracking-[0.25em] font-semibold text-white/80">Hotel Health</div>
          <HealthGauge score={health.score ?? 0} band={health.band ?? ""}/>
          <div className="text-center text-[11.5px] text-white/70 leading-relaxed">
            Composite score across{" "}
            {Object.keys(health.components || {}).length || 0} live signals.
          </div>
        </div>

        {/* ─── CENTER: Today's Focus stack ─── */}
        <div className="p-6 lg:p-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <Flame className="w-4 h-4 text-amber-300" strokeWidth={2}/>
              <h2 className="font-display text-[18px] font-semibold text-white">Today&apos;s Focus</h2>
            </div>
            <div className="text-[11px] uppercase tracking-widest text-white/70 font-semibold">
              {items.length ? `${items.length} item${items.length === 1 ? "" : "s"}` : "All clear"}
            </div>
          </div>

          {items.length === 0 ? (
            <AllClear health={health}/>
          ) : (
            <ol className="space-y-2.5" data-testid="focus-list">
              {items.map((item, idx) => (
                <FocusRow
                  key={item.key}
                  rank={idx + 1}
                  item={item}
                  onHandle={() => {
                    if (item.insight_id) ackInsight(item.insight_id);
                    onNavigate?.(item.to);
                  }}
                />
              ))}
            </ol>
          )}
        </div>

        {/* ─── RIGHT: Progress + at-a-glance chips ─── */}
        <div className="p-6 lg:p-7 border-t lg:border-t-0 lg:border-l border-white/[0.10] flex flex-col gap-4">
          <ProgressCard
            label="Occupancy today"
            value={kpis.occupancy_pct || 0}
            hint={`${kpis.occupied || 0} of ${kpis.total_rooms || 0} rooms`}
            color="#38bdf8"
            testid="focus-progress-occupancy"
          />
          <ProgressCard
            label="Rooms clean-ready"
            value={kpis.total_rooms ? Math.round(((kpis.rooms_ready || 0) / kpis.total_rooms) * 100) : 0}
            hint={`${kpis.rooms_ready || 0} ready · ${kpis.rooms_dirty || 0} dirty`}
            color="#10b981"
            testid="focus-progress-hk"
          />
          <div className="mt-2">
            <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-white/80 mb-2">Attention meter</div>
            <div className="flex items-center gap-1.5 flex-wrap">
              {["critical","high","medium","low"].map((sev) => {
                const c = insights.filter((i) => i.severity === sev).length;
                if (!c) return null;
                const color = sev === "critical" ? "bg-red-500/30 text-red-100 border-red-500/60"
                  : sev === "high" ? "bg-amber-500/30 text-amber-100 border-amber-500/60"
                  : sev === "medium" ? "bg-sky-500/30 text-sky-100 border-sky-500/60"
                  : "bg-emerald-500/30 text-emerald-100 border-emerald-500/60";
                return (
                  <span key={sev} className={`px-2 py-0.5 rounded-md text-[10.5px] uppercase tracking-wider font-semibold border ${color}`}>
                    {sev} · {c}
                  </span>
                );
              })}
              {!insights.length && (
                <span className="text-[12px] text-emerald-300">All quiet</span>
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}


function FocusRow({ rank, item, onHandle }) {
  const meta = MODULE_META[item.module] || MODULE_META.default;
  const Icon = meta.icon;
  const sevRing = item.severity === "critical" ? "ring-red-500/50 shadow-[0_0_25px_rgba(244,63,94,0.22)]"
    : item.severity === "high" ? "ring-amber-500/40"
    : item.severity === "medium" ? "ring-sky-500/40"
    : "ring-white/[0.14]";

  return (
    <li
      data-testid={`focus-item-${rank}`}
      className={`group relative rounded-2xl bg-white/[0.06] hover:bg-white/[0.10] transition-all ring-1 ${sevRing} overflow-hidden`}
    >
      <div className="px-4 py-3.5 flex items-start gap-4">
        {/* Rank */}
        <div className="w-8 h-8 rounded-lg bg-black/40 flex items-center justify-center shrink-0">
          <span className="font-display font-bold text-white text-[15px] leading-none tabular-nums">{rank}</span>
        </div>

        {/* Module icon */}
        <div
          className="w-9 h-9 rounded-xl flex items-center justify-center shrink-0"
          style={{ background: `${meta.color}28`, border: `1px solid ${meta.color}66` }}
        >
          <Icon className="w-[16px] h-[16px]" strokeWidth={2} style={{ color: meta.color }}/>
        </div>

        {/* Text */}
        <div className="flex-1 min-w-0">
          <div className="text-[14px] font-semibold text-white truncate">{item.title}</div>
          <div className="text-[12.5px] text-white/85 line-clamp-1 mt-0.5">{item.subtitle}</div>
          <div className="flex items-center gap-3 mt-1.5 text-[10.5px] uppercase tracking-wider text-white/65 font-semibold">
            <span>{item.module}</span>
            {item.impact_currency ? (
              <>
                <span>·</span>
                <span className="text-emerald-300 normal-case">≈ ₹{Math.round(item.impact_currency).toLocaleString("en-IN")}</span>
              </>
            ) : null}
            {item.confidence != null ? (
              <>
                <span>·</span>
                <span className="tabular-nums">Confidence {Math.round(item.confidence * 100)}%</span>
              </>
            ) : null}
          </div>
        </div>

        {/* Handle button */}
        <button
          onClick={onHandle}
          data-testid={`focus-handle-${rank}`}
          className="opacity-100 shrink-0 h-9 px-3.5 rounded-xl bg-white/15 hover:bg-white/25 text-white text-[12px] font-semibold inline-flex items-center gap-1.5 transition-all border border-white/20"
        >
          <span className="hidden sm:inline">{item.action.length > 24 ? "Handle it" : item.action}</span>
          <ArrowUpRight className="w-3.5 h-3.5"/>
        </button>
      </div>
    </li>
  );
}


function AllClear({ health }) {
  return (
    <div className="py-8 text-center" data-testid="focus-all-clear">
      <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-emerald-500/20 border border-emerald-500/50 mb-4">
        <CheckCircle2 className="w-6 h-6 text-emerald-300" strokeWidth={2}/>
      </div>
      <div className="font-display text-[22px] font-semibold text-white mb-1">You&apos;re on top of things.</div>
      <div className="text-[13px] text-white/80 max-w-md mx-auto leading-relaxed">
        No open insights, KPIs are inside healthy bands. Hotel Health at{" "}
        <span className="text-emerald-300 font-semibold">{health.score ?? 0}%</span>.
        {" "}A good moment to look forward — check reservations for the next 48 hours.
      </div>
    </div>
  );
}


function ProgressCard({ label, value, hint, color, testid }) {
  const v = Math.max(0, Math.min(100, value));
  return (
    <div data-testid={testid}>
      <div className="flex items-baseline justify-between mb-1">
        <div className="text-[10.5px] uppercase tracking-[0.2em] font-semibold text-white/80">{label}</div>
        <div className="text-white font-semibold text-[13px] tabular-nums"><CountUp value={v} decimals={1}/>%</div>
      </div>
      <div className="h-1.5 rounded-full bg-white/10 overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-[1400ms] ease-[cubic-bezier(0.22,1,0.36,1)]"
          style={{ width: `${v}%`, background: color, boxShadow: `0 0 12px ${color}66` }}
        />
      </div>
      <div className="text-[11px] text-white/60 mt-1">{hint}</div>
    </div>
  );
}
